<div class="viewer-toolbar__group" role="group" aria-label="Camera">
  <button type="button" class="viewer-toolbar__button" aria-label="Home view" (click)="viewer.moveCameraHome()">
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9.5h5v-5h3v5h5V10" />
    </svg>
    <span class="viewer-toolbar__tip">Home view</span>
  </button>
  <button type="button" class="viewer-toolbar__button" aria-label="Fit all" (click)="viewer.fitAll()">
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
      <rect x="8.5" y="8.5" width="7" height="7" rx="1" />
    </svg>
    <span class="viewer-toolbar__tip">Fit all</span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Zoom to the selection"
    [disabled]="viewer.selectedSceneNodeId === null"
    (click)="viewer.focusSelectedSceneNode()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l5 5M10.5 8v5M8 10.5h5" />
    </svg>
    <span class="viewer-toolbar__tip">Zoom to the selection <kbd>F</kbd></span>
  </button>
  <div class="viewer-toolbar__menu-anchor">
    <button
      type="button"
      class="viewer-toolbar__button"
      aria-label="Standard views"
      aria-haspopup="menu"
      [attr.aria-expanded]="viewsOpen"
      [class.viewer-toolbar__button--active]="viewsOpen"
      (click)="viewsOpen = !viewsOpen"
    >
      <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3 20 7.5v9L12 21l-8-4.5v-9z" />
        <path d="M4 7.5 12 12l8-4.5M12 12v9" />
      </svg>
      <span *ngIf="!viewsOpen" class="viewer-toolbar__tip">Standard views <kbd>1</kbd>–<kbd>6</kbd> <kbd>0</kbd></span>
    </button>
    <div *ngIf="viewsOpen" class="viewer-toolbar__menu" role="menu" aria-label="Standard views">
      <button
        *ngFor="let item of views"
        type="button"
        class="viewer-toolbar__menu-item"
        role="menuitem"
        (click)="showView(item.view)"
      >
        {{ item.label }} <kbd>{{ item.key }}</kbd>
      </button>
    </div>
  </div>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Parallel projection"
    [class.viewer-toolbar__button--active]="viewer.parallelProjection"
    [attr.aria-pressed]="viewer.parallelProjection"
    (click)="viewer.toggleParallelProjection()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h12M8 12h12M4 17h12" />
    </svg>
    <span class="viewer-toolbar__tip">
      {{ viewer.parallelProjection ? 'Back to perspective' : 'Parallel projection, for plans and elevations' }}
      <kbd>P</kbd>
    </span>
  </button>
</div>

<div class="viewer-toolbar__group" role="group" aria-label="Tools">
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Measure"
    [class.viewer-toolbar__button--active]="viewer.measurements.enabled"
    [attr.aria-pressed]="viewer.measurements.enabled"
    (click)="viewer.toggleMeasurement()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 16.5 16.5 3 21 7.5 7.5 21z" />
      <path d="M7 12.5l2 2M9.5 10l1.5 1.5M12 7.5l2 2M14.5 5l1.5 1.5" />
    </svg>
    <span class="viewer-toolbar__tip">Measure distances, angles, paths, areas and points</span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Section"
    [class.viewer-toolbar__button--active]="viewer.section.mode !== 'off'"
    [attr.aria-pressed]="viewer.section.mode !== 'off'"
    (click)="viewer.setSectionMode(viewer.section.mode === 'off' ? 'plane' : 'off')"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 20 7.5v9L12 21l-8-4.5v-9z" />
      <path d="M2.5 13.5 21.5 9" stroke-dasharray="2 2" />
    </svg>
    <span class="viewer-toolbar__tip">Section with a plane (settings in the Tools tab)</span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Section box"
    [class.viewer-toolbar__button--active]="viewer.section.mode === 'box'"
    [attr.aria-pressed]="viewer.section.mode === 'box'"
    (click)="viewer.section.mode === 'box' ? viewer.setSectionMode('off') : viewer.startSectionBox()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 7.5 16.5 10v5L12 17.5 7.5 15v-5z" />
      <path d="M3 7V3h4M17 3h4v4M21 17v4h-4M7 21H3v-4" />
    </svg>
    <span class="viewer-toolbar__tip">
      {{
        viewer.section.mode === 'box'
          ? 'Remove the section box'
          : viewer.selectedSceneNodeId === null
          ? 'Section box around the scene'
          : 'Section box around the selection'
      }}
    </span>
  </button>
  <button
    *ngIf="viewer.section.mode !== 'off'"
    type="button"
    class="viewer-toolbar__button"
    [attr.aria-label]="viewer.section.mode === 'box' ? 'Resize the section box' : 'Drag the section plane'"
    [class.viewer-toolbar__button--active]="viewer.sectionDragArmed"
    [attr.aria-pressed]="viewer.sectionDragArmed"
    (click)="viewer.toggleSectionDrag()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 12h18" />
      <path d="M12 3v6M9.5 5.5 12 3l2.5 2.5M12 21v-6M9.5 18.5 12 21l2.5-2.5" />
    </svg>
    <span class="viewer-toolbar__tip">
      {{
        viewer.section.mode === 'box'
          ? 'Drag a face of the box to resize it'
          : 'Drag on the view to move the section plane'
      }}
    </span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="X-ray"
    [class.viewer-toolbar__button--active]="viewer.xrayEnabled"
    [attr.aria-pressed]="viewer.xrayEnabled"
    (click)="viewer.toggleXray()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="3.5" width="11" height="11" rx="1.5" stroke-dasharray="2.5 2" />
      <rect x="9.5" y="9.5" width="11" height="11" rx="1.5" />
    </svg>
    <span class="viewer-toolbar__tip">X-ray: everything but the selection turns faint <kbd>X</kbd></span>
  </button>
</div>

<div class="viewer-toolbar__group" role="group" aria-label="Visibility">
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Hide the selection"
    [disabled]="viewer.selectedSceneNodeId === null"
    (click)="viewer.hideSelectedSceneNode()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M4 20 20 4" />
    </svg>
    <span class="viewer-toolbar__tip">Hide the selection <kbd>H</kbd></span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Show only the selection"
    [disabled]="viewer.selectedSceneNodeId === null"
    (click)="viewer.isolateSelectedSceneNode()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke-dasharray="2.5 2" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
    <span class="viewer-toolbar__tip">Show only the selection <kbd>I</kbd></span>
  </button>
  <button
    type="button"
    class="viewer-toolbar__button"
    aria-label="Show everything"
    (click)="viewer.unhideAllSceneNodes()"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
    <span class="viewer-toolbar__tip">Show everything <kbd>U</kbd></span>
  </button>
</div>

<div class="viewer-toolbar__group" role="group" aria-label="Help">
  <a
    class="viewer-toolbar__button"
    routerLink="/help"
    target="_blank"
    rel="noopener"
    aria-label="Viewer help (opens in a new tab)"
  >
    <svg class="viewer-toolbar__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6" />
      <path d="M12 17h.01" />
    </svg>
    <span class="viewer-toolbar__tip">Help: navigation, tools and tabs (new tab)</span>
  </a>
</div>
